// Renderer for the composable Block Kit widget.
// Contract: tools-framework-sdk/tools_framework_sdk/blocks/ — the bot sends a
// FULLY-RESOLVED tree (bindings substituted, repeats expanded) over the
// `ringg.blocks` text stream; this component only renders structure and
// collects discrete intents. Unknown block types render nothing (forward
// compat with a newer catalog). It mirrors the web renderer node for node —
// same catalog, same wire payload, same test ids — so the two files can be
// read side by side when the catalog grows.
//
// What the platform forces to differ, and nothing else:
//   - there is no `color-mix()` and no cascade: tints are blended here, and
//     every text node states its own colour, size and family
//   - there is no native `<select>` and no popup layer: selects expand inline,
//     in flow, pushing the rest of the tree down
//   - there is no date/time picker without a new dependency: those inputs are
//     typed, with the same themed affordance icon
//   - `window.open` / `CustomEvent` become `Linking.openURL` / the host handler
//   - a table cannot auto-size its columns: every column takes an equal share
//
// Embed rules kept from web:
//   - `markdown: true` text renders through the shared Markdown renderer
//   - https-only backstop on image URLs (§9 of the integration guide) — the
//     placeholder/broken chrome shows instead, without fetching
//   - unsupported schema_version renders a graceful fallback (§12)
//   - respond actions disable the tree while the RPC is in flight, and echo
//     the pressed button's label as a user bubble (label param on onAction)

import { Calendar, Check, ChevronDown, Clock, Image as ImageIcon, ImageOff } from "lucide-react-native";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type FC, type ReactElement, type ReactNode } from "react";
import { Animated, Image, Linking, ScrollView, StyleSheet, Text, TextInput, View, type KeyboardTypeOptions, type TextStyle, type ViewStyle } from "react-native";
import Svg, { Path } from "react-native-svg";
import type { ComponentTheme } from "@ringg/core";

import { SUPPORTED_BLOCKS_SCHEMA_VERSION, type BlockAction, type BlockNode, type BlockValue, type BlocksActionPayload, type BlocksConfig, type VisibleIf } from "../../lib/block-types";
import { GradientFill, solidColor } from "../../lib/gradient";
import { Markdown } from "../../lib/markdown";
import { ringgId } from "../../lib/testing";
import { mergeTheme, toNumber } from "../../lib/theme-utils";
import { resolveButtonRadius, resolveFontFamily, useWidgetTheme } from "../../lib/widget-theme";
import { Touchable } from "../ui/pressable";

/** The palette every node in one blocks tree draws from. */
type BlocksTheme = Required<ComponentTheme>;

/** RN spells the three catalog alignments the same way for items and self. */
type Align = "flex-start" | "center" | "flex-end";

/** Catalog widths are percentages of the parent. */
type Percent = `${number}%`;

// Widget state shared by all nodes of one blocks tree
interface BlocksState {
  getValue: (name: string, fallback?: unknown) => BlockValue | undefined;
  setValue: (name: string, value: BlockValue) => void;
  errors: Record<string, string>;
  disabled: boolean;
  submitRespond: (action: BlockAction, formNames: string[] | null, label?: string) => void;
  /** Sandbox hosts (dev panel / builder preview): receives open_url / dom_event instead of executing them. */
  onSandboxAction?: (action: BlockAction) => void;
}

interface BlocksStepProps {
  config: BlocksConfig;
  disabled?: boolean;
  onAction: (payload: BlocksActionPayload) => void | Promise<void>;
  onSandboxAction?: (action: BlockAction) => void;
}

interface NodeProps {
  node: BlockNode;
  // Input names of the nearest ancestor `form` (null = no form ancestor).
  formNames: string[] | null;
  inCarousel: boolean;
}

interface ImageBlockProps {
  node: BlockNode;
}

interface ExpanderBlockProps {
  node: BlockNode;
  /** Already-rendered body, so the expander never re-enters the renderer. */
  children: ReactNode;
}

interface ButtonBlockProps {
  node: BlockNode;
  formNames: string[] | null;
}

interface InputBlockProps {
  node: BlockNode;
}

interface CheckSquareProps {
  checked: boolean;
  theme: BlocksTheme;
}

interface PillToggleProps {
  label: string;
  selected: boolean;
  disabled?: boolean;
  fontSize: number;
  theme: BlocksTheme;
  onPress: () => void;
}

interface SelectDropdownProps {
  options: string[];
  selected: string[];
  placeholder: string;
  disabled?: boolean;
  /** Multi keeps the list open and shows check squares; single closes on pick. */
  multiple: boolean;
  fontSize: number;
  inputStyle: ViewStyle;
  theme: BlocksTheme;
  onSelect: (option: string) => void;
}

/** Button surface for one `style` value — background may be a gradient. */
interface ButtonVisual {
  background: string;
  color: string;
  borderColor: string;
}

const ALLOWED_URL_SCHEMES = ["https:", "mailto:", "tel:"];

const ALIGN: Record<string, Align> = { start: "flex-start", center: "center", end: "flex-end" };
const JUSTIFY: Record<string, ViewStyle["justifyContent"]> = { start: "flex-start", center: "center", end: "flex-end", between: "space-between" };
const TEXT_ALIGN: Record<string, TextStyle["textAlign"]> = { start: "left", center: "center", end: "right" };

// The keyboard is the only affordance an input_* type can ask for here; the
// DOM's `type=` also brought validation and pickers, which RN has no equivalent
// for (email format is checked on submit, same as web's own re-check).
const INPUT_KEYBOARD_TYPE: Record<string, KeyboardTypeOptions> = {
  input_email: "email-address",
  input_phone_number: "phone-pad",
  input_number: "numeric",
};

// Filled-star colour for input_rating — conventional amber, independent of the
// (possibly non-warm) brand primary so stars always read as a rating.
const RATING_COLOR = "#F5A623";

// Cards in a carousel are a fixed pitch, which is also what makes snapping
// possible — RN can only snap on a constant interval.
const CAROUSEL_CARD_WIDTH = 256;
const CAROUSEL_GAP = 12;

/** Web caps an unsized picture at `max-h-28`; RN needs it as a real height. */
const IMAGE_DEFAULT_HEIGHT = 112;

// Tailwind's animate-pulse: 2s, opacity 1 → .5 → 1.
const PULSE_HALF_CYCLE_MS = 1000;
const PULSE_MIN_OPACITY = 0.5;

/** Two rows of text, matching the web textarea's `rows={2}`. */
const TEXTAREA_MIN_HEIGHT = 60;

const DIVIDER_THICKNESS = 1.5;

/** Options and rows fill their own touch area — the default slop would overlap neighbours. */
const NO_HIT_SLOP = 0;

// A block node only ever renders under the provider below. The inert default
// keeps that fact out of the type system (and out of a non-null assertion): a
// node rendered standalone reads nothing and writes nowhere.
const NO_BLOCKS_STATE: BlocksState = {
  getValue: () => undefined,
  setValue: () => {},
  errors: {},
  disabled: true,
  submitRespond: () => {},
};

const BlocksStateContext = createContext<BlocksState>(NO_BLOCKS_STATE);

const useBlocksState = (): BlocksState => useContext(BlocksStateContext);

/**
 * Component palette for one blocks tree.
 *
 * Web reads this from a theme context that interactive components may override
 * per payload; blocks never carry a per-tree theme, so the widget theme is the
 * only input and `mergeTheme` supplies the component defaults for what a widget
 * theme has no opinion on (radius, padding, sizes). `fontFamily` is carried
 * over here even though web leaves it out: web inherits it through the cascade,
 * RN has to hand it to every text node.
 */
const useBlocksTheme = (): BlocksTheme => {
  const widget = useWidgetTheme();
  return useMemo(
    () =>
      mergeTheme({
        primaryColor: widget.primaryColor,
        primaryTextColor: widget.primaryTextColor,
        backgroundColor: widget.backgroundColor,
        surfaceColor: widget.surfaceColor,
        textColor: widget.textColor,
        mutedTextColor: widget.mutedTextColor,
        borderColor: widget.borderColor,
        errorColor: widget.errorColor,
        successColor: widget.successColor,
        buttonStyle: widget.buttonStyle,
        fontFamily: widget.fontFamily,
      }),
    [widget],
  );
};

// Names of all input nodes under a subtree (form submit scope).
const collectInputNames = (nodes: BlockNode[] | undefined): string[] => {
  const names: string[] = [];
  for (const node of nodes || []) {
    if (node.type.startsWith("input_") && node.name) names.push(node.name);
    names.push(...collectInputNames(node.children));
  }
  return names;
};

const findInputs = (nodes: BlockNode[] | undefined, scope: string[] | null): BlockNode[] => {
  const inputs: BlockNode[] = [];
  for (const node of nodes || []) {
    if (node.type.startsWith("input_") && node.name && (scope === null || scope.includes(node.name))) inputs.push(node);
    inputs.push(...findInputs(node.children, scope));
  }
  return inputs;
};

const evaluateVisibleIf = (condition: VisibleIf | undefined, getValue: BlocksState["getValue"]): boolean => {
  if (!condition) return true;
  if (condition.all) return condition.all.every((leaf) => evaluateVisibleIf(leaf, getValue));
  if (condition.any) return condition.any.some((leaf) => evaluateVisibleIf(leaf, getValue));
  if (!condition.field) return true;
  const actual = getValue(condition.field);
  switch (condition.op || "truthy") {
    case "eq":
      return actual === condition.value;
    case "ne":
      return actual !== condition.value;
    case "in":
      return Array.isArray(condition.value) && condition.value.includes(actual);
    case "gt":
      return typeof actual === "number" && typeof condition.value === "number" && actual > condition.value;
    case "lt":
      return typeof actual === "number" && typeof condition.value === "number" && actual < condition.value;
    case "truthy":
    default:
      return Array.isArray(actual) ? actual.length > 0 : !!actual;
  }
};

const percentWidth = (value: number): Percent => `${value}%`;

const fontSizeOf = (props: Record<string, unknown>, fallback: number): number => (typeof props.font_size === "number" ? props.font_size : fallback);

// Catalog 1.6 sizing model — numeric props instead of a discrete sm/md/lg
// scale: width is a % of the parent, height and font_size are dp. Layered on
// top of each block's defaults, so an unset prop keeps the original look and a
// set prop overrides it. `defaultFontPx` preserves the block's own text size
// when the author hasn't set font_size; omit it for boxes that hand the size to
// a child <Text> instead.
const sizeStyle = (props: Record<string, unknown>, defaultFontPx?: number): TextStyle => {
  const fontSize = typeof props.font_size === "number" ? props.font_size : defaultFontPx;
  // Built as one literal rather than mutated: RN's style types are readonly,
  // and an undefined entry would punch a hole in whatever this is spread over.
  return {
    ...(typeof props.width === "number" ? { width: percentWidth(props.width) } : null),
    ...(typeof props.height === "number" ? { height: props.height } : null),
    ...(fontSize !== undefined ? { fontSize } : null),
  };
};

// padding/gap are numeric dp (catalog 1.6) — resolve to the value, or the default.
const spacing = (value: unknown, fallback: number): number => (typeof value === "number" ? value : fallback);

const alignOf = (value: unknown): Align | undefined => (typeof value === "string" ? ALIGN[value] : undefined);

const justifyOf = (value: unknown): ViewStyle["justifyContent"] => (typeof value === "string" ? JUSTIFY[value] : undefined);

const textAlignOf = (value: unknown): TextStyle["textAlign"] => (typeof value === "string" ? TEXT_ALIGN[value] : undefined);

/** `#rgb` / `#rrggbb(aa)` / `rgb()` / `rgba()` → channels. Anything else is unknown. */
const parseColor = (color: string): [number, number, number] | undefined => {
  const value = color.trim();
  const hex = /^#([0-9a-f]+)$/i.exec(value)?.[1];
  if (hex) {
    // Shorthand doubles each digit (#abc → #aabbcc); an alpha byte is dropped.
    const six = hex.length === 3 || hex.length === 4 ? hex.slice(0, 3).replace(/./g, (digit) => digit + digit) : hex.length === 6 || hex.length === 8 ? hex.slice(0, 6) : "";
    if (!six) return undefined;
    const packed = Number.parseInt(six, 16);
    return Number.isFinite(packed) ? [(packed >> 16) & 0xff, (packed >> 8) & 0xff, packed & 0xff] : undefined;
  }
  const channels = /^rgba?\(([^)]+)\)$/i.exec(value)?.[1];
  if (!channels) return undefined;
  const parts = channels.split(/[\s,/]+/).filter(Boolean).slice(0, 3).map(Number);
  if (parts.length < 3 || parts.some((part) => !Number.isFinite(part))) return undefined;
  return [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0];
};

/**
 * `color-mix(in srgb, overlay <amount>, base)` for the four tinted surfaces in
 * this file. RN has no colour arithmetic, and layering a translucent view
 * instead would tint the block's children too. A colour we cannot read (a
 * gradient, a named CSS colour) returns `base` — the untinted look, never a
 * wrong one.
 */
const tint = (base: string, overlay: string, amount: number): string => {
  const from = parseColor(base);
  const to = parseColor(overlay);
  if (!from || !to) return base;
  const blend = (channel: 0 | 1 | 2): number => Math.round(from[channel] * (1 - amount) + to[channel] * amount);
  return `rgb(${blend(0)}, ${blend(1)}, ${blend(2)})`;
};

const headerFontSize = (level: number): number => {
  switch (level) {
    case 1:
      return 16;
    case 2:
      return 14;
    default:
      return 12;
  }
};

const badgeToneColor = (tone: string, theme: BlocksTheme): string => {
  switch (tone) {
    case "success":
      return theme.successColor;
    case "danger":
    case "warning":
      return theme.errorColor;
    case "info":
      return solidColor(theme.primaryColor);
    default:
      return theme.mutedTextColor;
  }
};

// Default/info picks up the brand primary so a callout doesn't read as a flat
// grey box; success/warning/error use their semantic colours.
const calloutAccent = (tone: string, theme: BlocksTheme): string => {
  switch (tone) {
    case "success":
      return solidColor(theme.successColor);
    case "warning":
    case "error":
      return solidColor(theme.errorColor);
    default:
      return solidColor(theme.primaryColor);
  }
};

const buttonVisual = (style: string, theme: BlocksTheme): ButtonVisual => {
  switch (style) {
    case "destructive":
      return { background: theme.errorColor, color: theme.primaryTextColor, borderColor: "transparent" };
    case "secondary":
      return { background: theme.surfaceColor, color: theme.textColor, borderColor: theme.borderColor };
    case "primary":
      return { background: theme.primaryColor, color: theme.primaryTextColor, borderColor: "transparent" };
    default:
      return { background: "transparent", color: theme.textColor, borderColor: theme.borderColor };
  }
};

const openExternalUrl = (url: string): void => {
  // Best effort: an unsupported scheme or a missing handler must not crash the
  // widget, and there is nothing useful to tell the user about it.
  void Linking.openURL(url).catch(() => {});
};

// Custom check square: brand-primary fill + tick when checked, themed border
// when not.
const CheckSquare: FC<CheckSquareProps> = ({ checked, theme }) => (
  <View
    style={[
      styles.checkSquare,
      checked
        ? { backgroundColor: solidColor(theme.primaryColor), borderColor: solidColor(theme.primaryColor) }
        : { backgroundColor: theme.backgroundColor, borderColor: solidColor(theme.borderColor) },
    ]}
  >
    {checked && (
      <Svg width={11} height={11} viewBox="0 0 12 12" fill="none">
        <Path d="M2.5 6.2 5 8.7l4.5-5.4" stroke={theme.primaryTextColor} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
      </Svg>
    )}
  </View>
);

// Chip used by the "pills" variant of single/multi select: selected = filled
// with the brand primary, unselected = outline.
const PillToggle: FC<PillToggleProps> = ({ label, selected, disabled, fontSize, theme, onPress }) => (
  <Touchable
    disabled={disabled}
    onPress={onPress}
    accessibilityLabel={label}
    hitSlop={NO_HIT_SLOP}
    style={[
      styles.pill,
      selected
        ? { backgroundColor: solidColor(theme.primaryColor), borderColor: solidColor(theme.primaryColor) }
        : { backgroundColor: "transparent", borderColor: solidColor(theme.borderColor) },
    ]}
  >
    <Text style={[styles.pillLabel, { color: selected ? theme.primaryTextColor : theme.textColor, fontSize, fontFamily: resolveFontFamily(theme.fontFamily) }]}>{label}</Text>
  </Touchable>
);

// One dropdown serves both selects: RN has no native picker, and the web
// multi-select menu was already modelled on the single-select trigger, so the
// anatomy is shared and only the picking rule differs.
const SelectDropdown: FC<SelectDropdownProps> = ({ options, selected, placeholder, disabled, multiple, fontSize, inputStyle, theme, onSelect }) => {
  const [open, setOpen] = useState(false);
  const font = resolveFontFamily(theme.fontFamily);
  const border = solidColor(theme.borderColor);

  return (
    <View>
      <Touchable disabled={disabled} onPress={() => setOpen((prev) => !prev)} accessibilityLabel={selected.length ? selected.join(", ") : placeholder} hitSlop={NO_HIT_SLOP} style={[styles.selectTrigger, inputStyle]}>
        <Text numberOfLines={1} style={[styles.selectValue, { color: selected.length ? theme.textColor : theme.mutedTextColor, fontSize, fontFamily: font }]}>
          {selected.length ? selected.join(", ") : placeholder}
        </Text>
        <ChevronDown size={14} color={theme.mutedTextColor} style={{ transform: [{ rotate: open ? "180deg" : "0deg" }] }} />
      </Touchable>
      {open && (
        // Web floats this menu over the page. RN has no overlay layer inside the
        // transcript and a nested vertical scroller would fight the transcript's
        // own, so the list expands in flow and pushes the tree below it down.
        <View style={[styles.selectMenu, { backgroundColor: theme.backgroundColor, borderColor: border, borderRadius: toNumber(theme.borderRadius, 8) }]}>
          {options.map((option, index) => {
            const isSelected = selected.includes(option);
            return (
              <Touchable
                key={`${option}-${index}`}
                disabled={disabled}
                onPress={() => {
                  onSelect(option);
                  if (!multiple) setOpen(false);
                }}
                accessibilityLabel={option}
                hitSlop={NO_HIT_SLOP}
                style={styles.selectOption}
              >
                {multiple && <CheckSquare checked={isSelected} theme={theme} />}
                <Text numberOfLines={1} style={[styles.selectOptionLabel, { color: theme.textColor, fontSize: 14, fontFamily: font }]}>
                  {option}
                </Text>
                {!multiple && isSelected && <Check size={14} color={solidColor(theme.primaryColor)} />}
              </Touchable>
            );
          })}
        </View>
      )}
    </View>
  );
};

// A bare picture hid itself on error and rendered nothing without a URL, which
// read as "images don't work". Instead: a labelled placeholder when no URL is
// set (or a ${{binding}} arrived unresolved), a pulse while bytes load, and a
// visible broken state for bad URLs. Non-https URLs take the broken state
// without ever being fetched (§9 of the integration guide).
const ImageBlock: FC<ImageBlockProps> = ({ node }) => {
  const theme = useBlocksTheme();
  const props = node.props || {};
  const url = typeof props.url === "string" ? props.url : "";
  const isBinding = url.includes("${{");
  const isBlockedScheme = !!url && !isBinding && !url.trim().toLowerCase().startsWith("https:");
  const [status, setStatus] = useState<"loading" | "loaded" | "error">("loading");
  const pulse = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    setStatus("loading");
  }, [url]);

  useEffect(() => {
    if (status !== "loading") return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: PULSE_MIN_OPACITY, duration: PULSE_HALF_CYCLE_MS, useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 1, duration: PULSE_HALF_CYCLE_MS, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse, status]);

  const radius = toNumber(theme.borderRadius, 8);
  const alignSelf = alignOf(props.align) ?? "flex-start";

  if (!url || isBinding || isBlockedScheme || status === "error") {
    const label = !url ? "Add an image URL" : isBinding ? url : "Couldn't load image";
    const broken = isBlockedScheme || (status === "error" && !isBinding);
    return (
      <View
        testID={ringgId("block-image")}
        style={[
          styles.imagePlaceholder,
          { alignSelf, borderColor: solidColor(theme.borderColor), borderRadius: radius, backgroundColor: tint(theme.backgroundColor, theme.surfaceColor, 0.5) },
          sizeStyle(props),
        ]}
      >
        {broken ? <ImageOff size={20} color={theme.mutedTextColor} /> : <ImageIcon size={20} color={theme.mutedTextColor} />}
        <Text numberOfLines={1} style={[styles.placeholderText, { color: theme.mutedTextColor, fontFamily: resolveFontFamily(theme.fontFamily) }]}>
          {label}
        </Text>
      </View>
    );
  }

  // An RN picture has no intrinsic size to grow into, so the box it is
  // contained in is always explicit: the author's numbers, or a full-width box
  // capped at web's `max-h-28`.
  const box: ViewStyle = {
    width: typeof props.width === "number" ? percentWidth(props.width) : "100%",
    height: typeof props.height === "number" ? props.height : IMAGE_DEFAULT_HEIGHT,
  };

  return (
    <View style={[styles.imageBox, box, { alignSelf, borderRadius: radius }]}>
      <Image
        key={url}
        testID={ringgId("block-image")}
        source={{ uri: url }}
        accessibilityLabel={String(props.alt ?? "")}
        resizeMode="contain"
        onLoad={() => setStatus("loaded")}
        onError={() => setStatus("error")}
        style={styles.imageFill}
      />
      {status === "loading" && <Animated.View testID={ringgId("block-image-loading")} style={[StyleSheet.absoluteFill, { backgroundColor: theme.surfaceColor, opacity: pulse }]} />}
    </View>
  );
};

// Actions: respond | open_url | dom_event
const ButtonBlock: FC<ButtonBlockProps> = ({ node, formNames }) => {
  const theme = useBlocksTheme();
  const state = useBlocksState();
  const props = node.props || {};
  const label = String(props.label ?? "");
  const press = node.on?.press;

  const handlePress = (): void => {
    if (!press || state.disabled) return;
    switch (press.action) {
      case "respond":
        state.submitRespond(press, formNames, label);
        return;
      case "open_url": {
        if (state.onSandboxAction) {
          state.onSandboxAction(press);
          return;
        }
        if (!press.url) return;
        const url = String(press.url);
        // `target` has no meaning here: RN always leaves the app for the
        // system browser, which is what `_blank` asked for anyway.
        if (ALLOWED_URL_SCHEMES.some((scheme) => url.trim().toLowerCase().startsWith(scheme))) openExternalUrl(url);
        return;
      }
      case "dom_event":
        // There is no ambient event bus outside the browser, so the host
        // handler is the only listener; without one the action is inert.
        state.onSandboxAction?.(press);
        return;
      default:
        // Unknown action strings are inert by contract (reserved for future use).
        return;
    }
  };

  const visual = buttonVisual(typeof props.style === "string" && props.style ? props.style : "primary", theme);

  return (
    <Touchable
      testID={ringgId("block-button")}
      accessibilityLabel={label}
      onPress={handlePress}
      disabled={state.disabled}
      hitSlop={NO_HIT_SLOP}
      // Unset width fills the line (what the builder's W control shows), so
      // side-by-side buttons in a row each need an explicit width%.
      style={[{ alignSelf: alignOf(props.align) }, sizeStyle(props)]}
    >
      <GradientFill color={visual.background} style={[styles.button, { borderColor: visual.borderColor, borderRadius: resolveButtonRadius(theme.buttonStyle) }]}>
        <Text style={[styles.buttonLabel, { color: visual.color, fontSize: fontSizeOf(props, 14), fontFamily: resolveFontFamily(theme.fontFamily) }]}>{label}</Text>
      </GradientFill>
    </Touchable>
  );
};

// input_* catalog types → same visuals as form-step
const InputBlock: FC<InputBlockProps> = ({ node }) => {
  const theme = useBlocksTheme();
  const state = useBlocksState();
  const props = node.props || {};
  const name = node.name ?? "";
  const error = state.errors[name];
  const font = resolveFontFamily(theme.fontFamily);

  // Inputs are keyed by name in the values bag; a name-less input (a config
  // error) would read and write an "" state key. Show a hint instead of
  // corrupting shared state.
  if (!name) {
    return (
      <View style={[styles.nameless, { borderColor: solidColor(theme.borderColor) }]}>
        <Text style={[styles.namelessText, { color: theme.mutedTextColor, fontFamily: font }]}>Input needs a name</Text>
      </View>
    );
  }

  const inputStyle: ViewStyle = {
    backgroundColor: theme.backgroundColor,
    borderColor: error ? solidColor(theme.errorColor) : solidColor(theme.borderColor),
    borderRadius: toNumber(theme.borderRadius, 8),
  };
  // Rating shows just stars unless a label is explicitly set; other inputs fall
  // back to the field name so they never render label-less.
  const label = String(props.label ?? (node.type === "input_rating" ? "" : name));
  // What web inherits from the wrapper's font size; text-sm controls state 14.
  const bodyFontSize = fontSizeOf(props, 14);
  const placeholder = String(props.placeholder ?? `Enter ${label.toLowerCase()}`);
  const selectPlaceholder = String(props.placeholder ?? `Select ${label.toLowerCase()}`);
  const options = (props.options as string[] | undefined) || [];

  const control = (): ReactElement => {
    switch (node.type) {
      case "input_checkbox": {
        const checked = (state.getValue(name) as boolean) || false;
        return (
          <Touchable disabled={state.disabled} onPress={() => state.setValue(name, !checked)} accessibilityLabel={label} hitSlop={NO_HIT_SLOP} style={styles.checkboxRow}>
            <CheckSquare checked={checked} theme={theme} />
            <Text style={{ color: theme.textColor, fontSize: 14, fontFamily: font }}>{label}</Text>
          </Touchable>
        );
      }
      case "input_rating": {
        const max = Math.min(Math.max(Math.round(Number(props.max) || 5), 3), 10);
        const current = Math.round(Number(state.getValue(name)) || 0);
        // font_size drives the star size (default 24 = the old md/text-2xl).
        const starPx = fontSizeOf(props, 24);
        return (
          <View style={[styles.ratingRow, { justifyContent: justifyOf(props.align) }]} accessibilityRole="radiogroup" accessibilityLabel={label}>
            {Array.from({ length: max }, (_unused, index) => {
              const filled = index < current;
              return (
                <Touchable
                  key={index}
                  disabled={state.disabled}
                  onPress={() => state.setValue(name, index + 1)}
                  accessibilityLabel={`${index + 1} star${index ? "s" : ""}`}
                  hitSlop={NO_HIT_SLOP}
                >
                  <Text style={{ color: filled ? RATING_COLOR : solidColor(theme.borderColor), fontSize: starPx, fontFamily: font }}>{filled ? "★" : "☆"}</Text>
                </Touchable>
              );
            })}
          </View>
        );
      }
      case "input_single_select": {
        const value = (state.getValue(name) as string) || "";
        if (props.variant === "pills") {
          return (
            <View style={[styles.pillRow, { justifyContent: justifyOf(props.align) }]}>
              {options.map((option, index) => (
                <PillToggle key={`${option}-${index}`} label={option} selected={value === option} disabled={state.disabled} fontSize={bodyFontSize} theme={theme} onPress={() => state.setValue(name, option)} />
              ))}
            </View>
          );
        }
        return (
          <SelectDropdown
            options={options}
            selected={value ? [value] : []}
            placeholder={selectPlaceholder}
            disabled={state.disabled}
            multiple={false}
            fontSize={bodyFontSize}
            inputStyle={inputStyle}
            theme={theme}
            onSelect={(option) => state.setValue(name, option)}
          />
        );
      }
      case "input_multi_select": {
        const current = (state.getValue(name) as string[]) || [];
        const toggle = (option: string): void => state.setValue(name, current.includes(option) ? current.filter((entry) => entry !== option) : [...current, option]);
        if (props.variant === "pills") {
          return (
            <View style={[styles.pillRow, { justifyContent: justifyOf(props.align) }]}>
              {options.map((option, index) => (
                <PillToggle key={`${option}-${index}`} label={option} selected={current.includes(option)} disabled={state.disabled} fontSize={bodyFontSize} theme={theme} onPress={() => toggle(option)} />
              ))}
            </View>
          );
        }
        return <SelectDropdown options={options} selected={current} placeholder={selectPlaceholder} disabled={state.disabled} multiple fontSize={14} inputStyle={inputStyle} theme={theme} onSelect={toggle} />;
      }
      case "input_textarea":
        return (
          <TextInput
            multiline
            placeholder={placeholder}
            placeholderTextColor={theme.mutedTextColor}
            accessibilityLabel={label}
            value={String(state.getValue(name) ?? "")}
            onChangeText={(text) => state.setValue(name, text)}
            editable={!state.disabled}
            style={[
              styles.field,
              styles.textarea,
              inputStyle,
              { color: theme.textColor, fontSize: bodyFontSize, fontFamily: font },
              // height sizes the control itself, not the label+error wrapper.
              typeof props.height === "number" ? { height: props.height } : null,
            ]}
          />
        );
      default: {
        const isDateOrTime = node.type === "input_date" || node.type === "input_time";
        const field = (
          <TextInput
            placeholder={placeholder}
            placeholderTextColor={theme.mutedTextColor}
            accessibilityLabel={label}
            value={String(state.getValue(name) ?? "")}
            keyboardType={INPUT_KEYBOARD_TYPE[node.type] ?? "default"}
            autoCapitalize={node.type === "input_email" ? "none" : "sentences"}
            autoCorrect={node.type !== "input_email"}
            onChangeText={(text) => {
              if (node.type !== "input_number") {
                state.setValue(name, text);
                return;
              }
              // The wire value stays numeric; text that doesn't parse collapses
              // to "", exactly what a DOM number input reports for it.
              const parsed = Number(text);
              state.setValue(name, text === "" || !Number.isFinite(parsed) ? "" : parsed);
            }}
            editable={!state.disabled}
            style={[styles.field, isDateOrTime && styles.fieldWithIcon, inputStyle, { color: theme.textColor, fontSize: 14, fontFamily: font }]}
          />
        );
        if (!isDateOrTime) return field;
        // No calendar/clock picker without a new dependency, so the date and
        // time fields are typed. The icon still marks what the field wants.
        const PickerIcon = node.type === "input_date" ? Calendar : Clock;
        return (
          <View>
            {field}
            <View pointerEvents="none" style={styles.fieldIcon}>
              <PickerIcon size={16} color={theme.mutedTextColor} />
            </View>
          </View>
        );
      }
    }
  };

  return (
    <View testID={ringgId(`block-${node.type}`)} style={sizeStyle({ ...props, height: undefined })}>
      {node.type !== "input_checkbox" && label.trim() !== "" && (
        <View style={styles.labelRow}>
          <Text style={[styles.label, { color: theme.mutedTextColor, fontFamily: font }]}>{label}</Text>
          {!!props.required && <Text style={[styles.label, { color: theme.errorColor, fontFamily: font }]}>*</Text>}
        </View>
      )}
      {control()}
      {!!error && <Text style={[styles.error, { color: theme.errorColor, fontFamily: font }]}>{error}</Text>}
    </View>
  );
};

// `<details>` has no RN counterpart, so the open/closed flag lives here. A
// collapsed body is unmounted rather than hidden: RN lays out `display: none`
// subtrees anyway, and a hidden ScrollView or image would still do work.
const ExpanderBlock: FC<ExpanderBlockProps> = ({ node, children }) => {
  const theme = useBlocksTheme();
  const props = node.props || {};
  const [open, setOpen] = useState(!!props.open);
  const font = resolveFontFamily(theme.fontFamily);
  const title = String(props.title ?? "");

  return (
    <View testID={ringgId(`block-${node.type}`)} style={[styles.expander, { borderColor: solidColor(theme.borderColor), borderRadius: toNumber(theme.borderRadius, 8) }, sizeStyle(props)]}>
      <Touchable onPress={() => setOpen((prev) => !prev)} accessibilityLabel={title} hitSlop={NO_HIT_SLOP} style={styles.expanderSummary}>
        <Text style={[styles.expanderTitle, { color: theme.textColor, fontSize: fontSizeOf(props, 14), fontFamily: font }]}>{title}</Text>
        <ChevronDown size={14} color={theme.mutedTextColor} style={{ transform: [{ rotate: open ? "180deg" : "0deg" }] }} />
      </Touchable>
      {open && <View style={styles.expanderBody}>{children}</View>}
    </View>
  );
};

const BlockRenderer: FC<NodeProps> = ({ node, formNames, inCarousel }) => {
  const theme = useBlocksTheme();
  const state = useBlocksState();
  const props = node.props || {};

  if (!evaluateVisibleIf(node.visible_if, state.getValue)) return null;

  const children = (scope: string[] | null, carousel = false): ReactElement[] =>
    (node.children || []).map((child, index) => <BlockRenderer key={`${child.id ?? "b"}-${index}`} node={child} formNames={scope} inCarousel={carousel} />);

  // RN carries one node identifier, so `data-block-id` has no counterpart —
  // `testID` keeps the name integrators already select on.
  const testID = ringgId(`block-${node.type}`);
  const font = resolveFontFamily(theme.fontFamily);
  const border = solidColor(theme.borderColor);
  const radius = toNumber(theme.borderRadius, 8);

  switch (node.type) {
    case "box":
      return (
        <View testID={testID} style={[styles.column, { alignItems: alignOf(props.align), gap: spacing(props.gap, 8), padding: spacing(props.padding, 0) }, sizeStyle(props)]}>
          {children(formNames)}
        </View>
      );
    case "column":
      return (
        <View testID={testID} style={[styles.column, { alignItems: alignOf(props.align), gap: spacing(props.gap, 12) }, sizeStyle(props)]}>
          {children(formNames)}
        </View>
      );
    case "row":
      return (
        <View
          testID={testID}
          style={[
            styles.row,
            { alignItems: alignOf(props.align) ?? "center", justifyContent: justifyOf(props.justify), flexWrap: props.wrap === false ? "nowrap" : "wrap", gap: spacing(props.gap, 8) },
            sizeStyle(props),
          ]}
        >
          {children(formNames)}
        </View>
      );
    case "form":
      return (
        <View testID={testID} style={[styles.form, sizeStyle(props)]}>
          {children(collectInputNames(node.children))}
        </View>
      );
    case "card": {
      // Trailing buttons pin to the card bottom — in a carousel the cards
      // stretch to the tallest sibling, so CTAs align across cards regardless
      // of how much content sits above them. Outside a carousel the card hugs
      // its content and the wrapper changes nothing.
      const kids = node.children || [];
      let bodyEnd = kids.length;
      while (bodyEnd > 0 && kids[bodyEnd - 1]?.type === "button") bodyEnd -= 1;
      const renderKids = (list: BlockNode[], offset: number): ReactElement[] =>
        list.map((child, index) => <BlockRenderer key={`${child.id ?? "b"}-${offset + index}`} node={child} formNames={formNames} inCarousel={false} />);
      return (
        <View
          testID={testID}
          style={[
            styles.card,
            { alignItems: alignOf(props.align), backgroundColor: theme.backgroundColor, borderColor: border, borderRadius: radius, padding: spacing(props.padding, 12) },
            inCarousel && styles.carouselCard,
            sizeStyle(props),
          ]}
        >
          {renderKids(kids.slice(0, bodyEnd), 0)}
          {bodyEnd < kids.length && <View style={styles.cardFooter}>{renderKids(kids.slice(bodyEnd), bodyEnd)}</View>}
        </View>
      );
    }
    case "carousel":
      return (
        // alignItems stretch (explicit): every card matches the tallest one.
        // Snapping runs on the fixed card pitch — the only interval RN can snap
        // to, and the only child width the catalog pins down.
        <ScrollView
          testID={testID}
          horizontal
          showsHorizontalScrollIndicator={false}
          snapToInterval={CAROUSEL_CARD_WIDTH + CAROUSEL_GAP}
          snapToAlignment="start"
          decelerationRate="fast"
          style={[styles.carousel, sizeStyle(props)]}
          contentContainerStyle={styles.carouselContent}
        >
          {children(formNames, true)}
        </ScrollView>
      );
    case "header":
      return (
        <Text testID={testID} style={[styles.header, { color: theme.textColor, fontFamily: font, textAlign: textAlignOf(props.align) }, sizeStyle(props, headerFontSize(Number(props.level) || 1))]}>
          {String(props.text ?? "")}
        </Text>
      );
    case "text": {
      if (props.markdown === true) {
        return (
          // Markdown renders a stack of blocks, so `align` places those blocks
          // in the column; it cannot re-align the wrapped lines inside them.
          <View testID={testID} style={[{ alignItems: alignOf(props.align) }, sizeStyle(props)]}>
            <Markdown
              content={String(props.text ?? "")}
              color={theme.textColor}
              mutedColor={theme.mutedTextColor}
              linkColor={solidColor(theme.primaryColor)}
              fontSize={fontSizeOf(props, 14)}
              fontFamily={font}
            />
          </View>
        );
      }
      return (
        <Text testID={testID} style={[{ color: theme.textColor, fontFamily: font, textAlign: textAlignOf(props.align) }, sizeStyle(props, 14)]}>
          {String(props.text ?? "")}
        </Text>
      );
    }
    case "image":
      return <ImageBlock node={node} />;
    case "divider":
      // width % shortens the rule; height sets its thickness.
      return (
        <View
          testID={testID}
          style={[
            styles.divider,
            {
              alignSelf: alignOf(props.align),
              backgroundColor: tint(border, "#000000", 0.08),
              height: typeof props.height === "number" ? props.height : DIVIDER_THICKNESS,
              width: typeof props.width === "number" ? percentWidth(props.width) : "100%",
            },
          ]}
        />
      );
    case "facts": {
      const items = (props.items as Array<{ label: string; value: string }> | undefined) || [];
      const fontSize = fontSizeOf(props, 14);
      return (
        <View testID={testID} style={[styles.facts, sizeStyle(props)]}>
          {items.map((item, index) => (
            <View key={index} style={styles.factsRow}>
              <Text style={{ color: theme.mutedTextColor, fontSize, fontFamily: font }}>{item.label}</Text>
              <Text style={[styles.factsValue, { color: theme.textColor, fontSize, fontFamily: font }]}>{item.value}</Text>
            </View>
          ))}
        </View>
      );
    }
    case "badge": {
      const toneColor = badgeToneColor(String(props.tone ?? "neutral"), theme);
      return (
        <View testID={testID} style={[styles.badge, { alignSelf: alignOf(props.align) ?? "flex-start", borderColor: toneColor }, sizeStyle(props)]}>
          <Text numberOfLines={1} style={[styles.badgeText, { color: toneColor, fontSize: fontSizeOf(props, 12), fontFamily: font }]}>
            {String(props.text ?? "")}
          </Text>
        </View>
      );
    }
    case "table": {
      const columns = (props.columns as string[] | undefined) || [];
      // rows must be an array of arrays. Guard against an unresolved ${{binding}}
      // string AND against array-of-objects (wrong shape) — filter to array rows
      // so neither reaches the inner map and crashes the widget.
      const rows = (Array.isArray(props.rows) ? (props.rows as unknown[]) : []).filter(Array.isArray) as Array<Array<string | number | boolean>>;
      const cellFontSize = fontSizeOf(props, 14);
      const cellBorder = { borderBottomWidth: 1, borderColor: border };
      return (
        // Card chrome (surface, border, theme radius) so the table reads like
        // the other block widgets instead of bare rows floating in the chat.
        // Columns share the width evenly and cells wrap: RN has no auto table
        // layout, and equal shares are the only split that keeps every row's
        // cells aligned without measuring text.
        <View testID={testID} style={[styles.table, { backgroundColor: tint(theme.backgroundColor, theme.surfaceColor, 0.5), borderColor: border, borderRadius: radius }, sizeStyle(props)]}>
          <View style={styles.row}>
            {columns.map((column, index) => (
              <View key={index} style={[styles.tableCell, cellBorder]}>
                <Text numberOfLines={1} style={[styles.tableHeaderText, { color: theme.mutedTextColor, fontFamily: font }]}>
                  {column}
                </Text>
              </View>
            ))}
          </View>
          {rows.map((row, rowIndex) => (
            <View key={rowIndex} style={styles.row}>
              {row.map((cell, cellIndex) => (
                // Last row keeps the card's own edge — no trailing border line.
                <View key={cellIndex} style={[styles.tableCell, rowIndex < rows.length - 1 && cellBorder]}>
                  <Text style={{ color: theme.textColor, fontSize: cellFontSize, fontFamily: font }}>{String(cell)}</Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      );
    }
    case "steps": {
      const items = (props.items as string[] | undefined) || [];
      const current = Math.max(1, Math.min(Number(props.current) || 1, items.length));
      const fontSize = fontSizeOf(props, 15);
      return (
        // align moves the whole tracker as a unit so rows stay internally
        // left-aligned and the dots keep a straight vertical line. compact
        // (default): natural gap. stretch: spread steps to fill the container
        // height (pairs with the height prop).
        <View
          testID={testID}
          style={[styles.column, { alignSelf: alignOf(props.align) }, props.layout === "stretch" ? styles.stepsStretch : styles.stepsCompact, sizeStyle(props)]}
        >
          {items.map((label, index) => {
            const stepNo = index + 1;
            const done = stepNo < current;
            const active = stepNo === current;
            const dotColor = done || active ? solidColor(theme.primaryColor) : border;
            return (
              <View key={index} style={styles.stepRow}>
                <View style={[styles.stepDot, { backgroundColor: done || active ? dotColor : "transparent", borderColor: dotColor }]}>
                  <Text style={[styles.stepIndex, { color: done || active ? theme.primaryTextColor : theme.mutedTextColor, fontFamily: font }]}>{done ? "✓" : String(stepNo)}</Text>
                </View>
                <Text style={[styles.stepLabel, { color: active ? theme.textColor : theme.mutedTextColor, fontWeight: active ? "600" : "400", fontSize, fontFamily: font }]}>{label}</Text>
              </View>
            );
          })}
        </View>
      );
    }
    case "callout": {
      const accent = calloutAccent(String(props.tone ?? "info"), theme);
      const fontSize = fontSizeOf(props, 14);
      const textAlign = textAlignOf(props.align);
      return (
        // Soft brand-tinted card; the tint + accent title carry the tone.
        <View testID={testID} style={[styles.callout, { backgroundColor: tint(theme.backgroundColor, accent, 0.07), borderRadius: radius }, sizeStyle(props)]}>
          {!!props.title && <Text style={[styles.calloutTitle, { color: accent, fontFamily: font, textAlign }]}>{String(props.title)}</Text>}
          <Text style={{ color: theme.textColor, fontSize, lineHeight: fontSize * 1.625, fontFamily: font, textAlign }}>{String(props.text ?? "")}</Text>
        </View>
      );
    }
    case "expander":
      return <ExpanderBlock node={node}>{children(formNames)}</ExpanderBlock>;
    case "button":
      return <ButtonBlock node={node} formNames={formNames} />;
    default:
      if (node.type.startsWith("input_")) return <InputBlock node={node} />;
      // Unknown type: render children if any (container-ish), else nothing.
      return node.children?.length ? <>{children(formNames)}</> : null;
  }
};

const BlocksStep: FC<BlocksStepProps> = ({ config, disabled, onAction, onSandboxAction }) => {
  const theme = useBlocksTheme();
  const [values, setValues] = useState<Record<string, BlockValue>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Newer envelope than we understand — degrade instead of guessing (§12)
  if ((config.schema_version ?? 1) > SUPPORTED_BLOCKS_SCHEMA_VERSION) {
    return (
      <Text testID={ringgId("blocks-unsupported")} style={[styles.unsupported, { color: theme.mutedTextColor, fontFamily: resolveFontFamily(theme.fontFamily) }]}>
        This content needs a newer version of the widget.
      </Text>
    );
  }

  const defaultsOf = (name: string): unknown => findInputs(config.blocks, [name])[0]?.props?.default;

  const getValue: BlocksState["getValue"] = (name, fallback) => {
    if (name in values) return values[name];
    const fromProps = defaultsOf(name);
    return (fromProps as BlockValue | undefined) ?? (fallback as BlockValue | undefined);
  };

  const setValue = (name: string, value: BlockValue): void => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const submitRespond = (action: BlockAction, formNames: string[] | null, label?: string): void => {
    if (!action.action_id) return;
    // Validate required inputs within the submit scope (visible ones only —
    // fields hidden by visible_if are intentionally not filled).
    const scopeInputs = findInputs(config.blocks, formNames).filter((input) => evaluateVisibleIf(input.visible_if, getValue));
    const nextErrors: Record<string, string> = {};
    const collected: Record<string, BlockValue> = {};
    for (const input of scopeInputs) {
      const name = input.name;
      if (!name) continue;
      const value = getValue(name);
      const isEmpty = value === undefined || value === "" || (Array.isArray(value) && value.length === 0);
      if ((input.props?.required as boolean) && isEmpty) {
        nextErrors[name] = `${(input.props?.label as string) || name} is required`;
        continue;
      }
      if (input.type === "input_email" && typeof value === "string" && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        nextErrors[name] = "Please enter a valid email address";
        continue;
      }
      if (!isEmpty && value !== undefined) collected[name] = value;
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    const result = onAction({ action_id: action.action_id, value: action.value, values: collected, label });
    if (result instanceof Promise) {
      setSubmitting(true);
      void result.finally(() => setSubmitting(false));
    }
  };

  const state: BlocksState = { getValue, setValue, errors, disabled: !!disabled || submitting, submitRespond, onSandboxAction };

  return (
    <BlocksStateContext.Provider value={state}>
      <View testID={ringgId("blocks")} style={styles.blocks}>
        {config.blocks.map((node, index) => (
          <BlockRenderer key={`${node.id ?? "b"}-${index}`} node={node} formNames={null} inCarousel={false} />
        ))}
      </View>
    </BlocksStateContext.Provider>
  );
};

const styles = StyleSheet.create({
  badge: { flexDirection: "row", alignItems: "center", borderWidth: 1, borderRadius: 9999, paddingHorizontal: 8, paddingVertical: 2 },
  badgeText: { fontWeight: "600" },
  blocks: { width: "100%", flexDirection: "column", gap: 12 },
  button: { flexDirection: "row", alignItems: "center", justifyContent: "center", borderWidth: 1, paddingHorizontal: 16, paddingVertical: 8 },
  buttonLabel: { fontWeight: "600", textAlign: "center" },
  callout: { flexDirection: "column", gap: 6, paddingHorizontal: 16, paddingVertical: 12 },
  // tracking-wider (0.05em) at the title's 11px.
  calloutTitle: { fontSize: 11, fontWeight: "600", textTransform: "uppercase", letterSpacing: 0.55 },
  card: { flexDirection: "column", gap: 8, borderWidth: 1 },
  cardFooter: { marginTop: "auto", flexDirection: "column", gap: 8 },
  carousel: { marginHorizontal: -4 },
  carouselCard: { width: CAROUSEL_CARD_WIDTH, flexShrink: 0 },
  carouselContent: { flexDirection: "row", alignItems: "stretch", gap: CAROUSEL_GAP, paddingHorizontal: 4, paddingBottom: 8 },
  checkSquare: { width: 18, height: 18, flexShrink: 0, alignItems: "center", justifyContent: "center", borderWidth: 1, borderRadius: 5 },
  checkboxRow: { flexDirection: "row", alignItems: "center", gap: 10, paddingVertical: 4 },
  column: { flexDirection: "column" },
  divider: { flexShrink: 0 },
  error: { fontSize: 12, marginTop: 2 },
  expander: { borderWidth: 1, overflow: "hidden" },
  expanderBody: { flexDirection: "column", gap: 8, paddingHorizontal: 12, paddingBottom: 12 },
  expanderSummary: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 8, paddingHorizontal: 12, paddingVertical: 8 },
  expanderTitle: { flexShrink: 1, fontWeight: "500" },
  facts: { flexDirection: "column", gap: 4 },
  factsRow: { flexDirection: "row", alignItems: "baseline", justifyContent: "space-between", gap: 12 },
  factsValue: { flexShrink: 1, textAlign: "right", fontWeight: "500" },
  field: { borderWidth: 1, paddingHorizontal: 12, paddingVertical: 10 },
  fieldIcon: { position: "absolute", right: 12, top: 0, bottom: 0, justifyContent: "center" },
  fieldWithIcon: { minHeight: 42, paddingRight: 40 },
  form: { flexDirection: "column", gap: 12 },
  header: { fontWeight: "600" },
  imageBox: { overflow: "hidden" },
  imageFill: { width: "100%", height: "100%" },
  imagePlaceholder: {
    minHeight: 80,
    width: "100%",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    borderWidth: 1,
    borderStyle: "dashed",
    paddingHorizontal: 12,
    paddingVertical: 16,
  },
  label: { fontSize: 12, fontWeight: "500" },
  labelRow: { flexDirection: "row", alignItems: "center", gap: 4, marginBottom: 4 },
  nameless: { borderWidth: 1, borderStyle: "dashed", borderRadius: 4, paddingHorizontal: 12, paddingVertical: 8 },
  namelessText: { fontSize: 12 },
  pill: { borderWidth: 1, borderRadius: 9999, paddingHorizontal: 14, paddingVertical: 6 },
  pillLabel: { fontWeight: "500" },
  pillRow: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  placeholderText: { maxWidth: "100%", fontSize: 12 },
  ratingRow: { flexDirection: "row", alignItems: "center", gap: 6 },
  row: { flexDirection: "row" },
  selectMenu: { borderWidth: 1, marginTop: 4, paddingVertical: 4 },
  selectOption: { flexDirection: "row", alignItems: "center", gap: 10, paddingHorizontal: 12, paddingVertical: 8 },
  selectOptionLabel: { flex: 1 },
  selectTrigger: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 8, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 10 },
  selectValue: { flex: 1 },
  stepDot: { width: 20, height: 20, flexShrink: 0, alignItems: "center", justifyContent: "center", borderWidth: 1.5, borderRadius: 9999 },
  stepIndex: { fontSize: 10, fontWeight: "600" },
  stepLabel: { flexShrink: 1 },
  stepRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  stepsCompact: { gap: 8 },
  stepsStretch: { justifyContent: "space-between" },
  table: { overflow: "hidden", borderWidth: 1 },
  tableCell: { flex: 1, paddingHorizontal: 12, paddingVertical: 8 },
  tableHeaderText: { fontSize: 12, fontWeight: "600" },
  textarea: { minHeight: TEXTAREA_MIN_HEIGHT, textAlignVertical: "top" },
  unsupported: { fontSize: 15 },
});

export default BlocksStep;
